import Avatar from 'components/base/Avatar';
import { Card, Dropdown, Form, Nav } from 'react-bootstrap';
import avatar from 'assets/img/team/avatar.webp';
import FeatherIcon from 'feather-icons-react';
import { Link, useNavigate } from 'react-router-dom';
import Scrollbar from 'components/base/Scrollbar';
import classNames from 'classnames';
import { logoutUser } from 'api/auth/_requests';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from 'redux-state/auth/authSlice';
import { setItemToStore, copyToClipboard, PF } from 'helpers/utils';
import { RootState } from 'redux-state/store';
import toast from 'react-hot-toast';
import { queryClient } from 'index';

const ProfileDropdownMenu = ({
  className,
  showProfile,
  setShowProfile
}: {
  className?: string;
  showProfile: boolean;
  setShowProfile: (showProfile: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const navItems = [
    { label: 'Profile', icon: 'user', link: '/profile' },
    {
      label: 'Dashboard',
      icon: 'pie-chart',
      link: user?.role === 'vendor' ? '/dashboard/e-commerce' : '/'
    },
    { label: 'My Orders', icon: 'shopping-cart', link: '/my-orders' },
    { label: 'Track My Orders', icon: 'map-pin', link: '/order-tracking' },
    {
      label: 'My Products',
      icon: 'shopping-cart',
      link: user?.role === 'vendor' ? '/vendor-products' : '/my-products'
    },
    { label: 'Settings', icon: 'settings', link: '' },
    { label: 'Help Center', icon: 'help-circle', link: '' }
  ];

  const handleCloseMenu = (callback?: () => void) => {
    setShowProfile(false);
    if (callback) callback();
  };

  const handleSignOut = async () => {
    handleCloseMenu(async () => {
      try {
        navigate('/', { replace: true });
        await logoutUser();
        dispatch(setUser(null));
        queryClient.invalidateQueries();
        setItemToStore('user', '');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    });
  };

  const copyVendorId = () => {
    copyToClipboard(user?.id || '');
    toast.success('Vendor ID copied to clipboard');
  };

  return (
    <Dropdown show={showProfile} onToggle={() => setShowProfile(!showProfile)}>
      <Dropdown.Toggle className="dropdown-caret-none nav-link lh-1" variant="">
        <FeatherIcon icon="user" size={20} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        align="end"
        style={{ zIndex: 1060 }}
        className={classNames(
          className,
          'navbar-top-dropdown-menu navbar-dropdown-caret py-0 dropdown-profile shadow border'
        )}
        onClick={() => setShowProfile(false)}
      >
        <Card className="position-relative border-0" style={{ zIndex: 9999 }}>
          <Card.Body className="p-0">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2 pt-4 pb-3">
              <Link to="/profile" onClick={() => handleCloseMenu()}>
                <Avatar
                  src={user?.avatar ? PF + user?.avatar : avatar}
                  size="xl"
                />
              </Link>
              <h6 className="text-body-emphasis">{user?.fullName}</h6>

              {user?.id && user.role === 'vendor' && (
                <div className="d-flex justify-content-evenly align-items-center w-100">
                  <p className="text-body-quaternary mb-0">{user.id}</p>
                  <button
                    type="button"
                    onClick={copyVendorId}
                    title="Copy vendor ID"
                    className="bg-transparent border-0"
                  >
                    <FeatherIcon
                      icon="copy"
                      size={16}
                      className="text-body-quaternary"
                    />
                  </button>
                </div>
              )}
            </div>

            <div className="mb-3 mx-3">
              <Form.Control
                type="text"
                placeholder="Update your status"
                size="sm"
              />
            </div>

            {user && (
              <div style={{ height: '10rem' }}>
                <Scrollbar>
                  <Nav className="nav flex-column mb-2">
                    {navItems.map(item => (
                      <Nav.Item className="mt-3" key={item.label}>
                        <Nav.Link
                          as={Link}
                          to={item.link}
                          className="px-3 py-1"
                          onClick={() => handleCloseMenu()}
                        >
                          <FeatherIcon
                            icon={item.icon}
                            size={16}
                            className="me-2 text-body"
                          />
                          <span className="text-body-highlight">
                            {item.label}
                          </span>
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </Scrollbar>
              </div>
            )}
          </Card.Body>

          <Card.Footer className="p-0 border-top border-translucent">
            <div className="px-3">
              {user ? (
                <button
                  type="button"
                  className="btn btn-phoenix-secondary d-flex flex-center w-100 mt-2"
                  onClick={handleSignOut}
                >
                  <FeatherIcon icon="log-out" size={16} className="me-2" />
                  Sign out
                </button>
              ) : (
                <Link
                  to="/sign-in"
                  className="btn btn-phoenix-secondary d-flex flex-center w-100"
                  onClick={() => handleCloseMenu()}
                >
                  <FeatherIcon icon="log-in" size={16} className="me-2" />
                  Sign in
                </Link>
              )}
            </div>

            {!user && (
              <div className="px-3 mt-2">
                <Link
                  to="/sign-up"
                  className="btn btn-primary text-white d-flex flex-center w-100"
                  onClick={() => handleCloseMenu()}
                >
                  <FeatherIcon icon="user-plus" size={16} className="me-2" />
                  Sign up
                </Link>
              </div>
            )}

            <div className="my-2 text-center fw-bold fs-10 text-body-quaternary">
              <Link className="text-body-quaternary me-1" to="#!">
                Privacy policy
              </Link>
              •
              <Link className="text-body-quaternary mx-1" to="#!">
                Terms
              </Link>
              •
              <Link className="text-body-quaternary ms-1" to="#!">
                Cookies
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ProfileDropdownMenu;
